import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { handleApi } from "../../utils/authentication";
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaRegEyeSlash, FaRegEye } from "react-icons/fa";
import logo from "../../assests/logo.png";
import group from "../../assests/group.png";
import { useSelector } from "react-redux";
import { getClientLandingPath } from "../../utils/clientRouting";
import "../../components/login.css"; // Using the same CSS as login

const ClientSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const user = useSelector((state) => state?.user);

  const handleClientSignup = async () => {
    try {
      setLoading(true);
      let method = "POST";
      let headers = {
        "Content-Type": "application/json",
      };
      let data = JSON.stringify({
        client_name: formik.values.client_name,
        first_name: formik.values.first_name,
        last_name: formik.values.last_name,
        email: formik.values.email,
        password: formik.values.password,
        confirm_password: formik.values.confirm_password,
        website: formik.values.website || null,
        total_members: formik.values.total_members,
        is_agency: formik.values.is_agency === "yes" ? 1 : 0,
        // Destination for the "Accept Invitation" CTA in the welcome email.
        // The user already set their password during signup, so we send them to login.
        login_link: `${process.env.REACT_APP_URL}client/login`
      });

      let result = await handleApi('client_signup', method, headers, data);

      if (result?.success) {
        toast.success('Account Created Successfully. You can now login with your credentials.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate('/client/login');
      } else {
        toast.error(result?.error?.error_message ? result?.error?.error_message : "Something went wrong! Please Try again later", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    } catch (e) {
      toast.error(e.message ? e.message : "Something went wrong! Please Try again later", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = Yup.object({
    client_name: Yup.string().required('Client Name is Required'),
    first_name: Yup.string().required('Firstname is Required').matches(/^[A-Za-z]+$/, 'Only letters are allowed'),
    last_name: Yup.string().required('Lastname is Required').matches(/^[A-Za-z]+$/, 'Only letters are allowed'),
    email: Yup.string().email('Invalid email address').required('Email is Required'),
    password: Yup.string()
      .required('Password is Required')
      .min(6, 'Password must be at least 6 characters'),
    confirm_password: Yup.string()
      .required('Confirm Password is Required')
      .oneOf([Yup.ref('password')], 'Passwords must match'),
    total_members: Yup.number().required('Total Members is Required').moreThan(0, 'Number must be greater than 0'),
    is_agency: Yup.string().required('Please select if this is an agency')
  });

  const formik = useFormik({
    initialValues: {
      client_name: '',
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirm_password: '',
      website: '',
      total_members: 1,
      is_agency: "no"
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleClientSignup();
    },
  });

  // Check if user is already logged in
  if (user?.isLoggedIn && user?.is_client) {
    return <Navigate to={getClientLandingPath(user)} replace />;
  }

  return (
    <div className="login_main" style={{ maxHeight: '100vh', overflowY: 'auto', paddingTop: '2rem' }}>
      <form onSubmit={formik.handleSubmit}>
        <div className="d-flex gap-2 align-items-center mb-5">
        <img
          src={logo}
          alt=""
          className=""
          style={{ width: "2.3rem", height: "auto" }}
        />
        <div className={`login_company_logo d-flex justify-content-center flex-column`}>
          <p className="mb-0">University </p>
          <p className="mb-0">Intelligence</p>
        </div>
      </div>

      <div id="login_card">
        <div className="login_container">
          <p id="login_heading">
            <span>Create</span> Account
          </p>

          {/* Organization Information */}
          <div className="mb-3">
            <label htmlFor="client_name" className="form-label">
              Client Name (University/Organization Name)
            </label>
            <input
              type="text"
              className="form-control input"
              id="client_name"
              placeholder="Enter Client Name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.client_name}
            />
            {formik.touched.client_name && formik.errors.client_name ? (
              <div className="text-danger small">{formik.errors.client_name}</div>
            ) : null}
          </div>

          {/* Admin Name Row */}
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="first_name" className="form-label">
                Admin First Name
              </label>
              <input
                type="text"
                className="form-control input"
                id="first_name"
                placeholder="Enter Firstname"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.first_name}
              />
              {formik.touched.first_name && formik.errors.first_name ? (
                <div className="text-danger small">{formik.errors.first_name}</div>
              ) : null}
            </div>
            <div className="col-md-6">
              <label htmlFor="last_name" className="form-label">
                Admin Last Name
              </label>
              <input
                type="text"
                className="form-control input"
                id="last_name"
                placeholder="Enter Lastname"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.last_name}
              />
              {formik.touched.last_name && formik.errors.last_name ? (
                <div className="text-danger small">{formik.errors.last_name}</div>
              ) : null}
            </div>
          </div>

          {/* Email */}
          <div className="mb-3">
            <label htmlFor="email" className="form-label">
              Admin Email
            </label>
            <input
              type="email"
              className="form-control input"
              id="email"
              placeholder="Enter Email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
            />
            {formik.touched.email && formik.errors.email ? (
              <div className="text-danger small">{formik.errors.email}</div>
            ) : null}
          </div>

          {/* Password Row */}
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="d-flex justify-content-center align-items-center position-relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control input"
                  id="password"
                  placeholder="Min 6 characters"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                />
                <span
                  className="eye_btn"
                  onClick={() => {
                    setShowPassword((prev) => !prev);
                  }}
                >
                  {!showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </span>
              </div>
              {formik.touched.password && formik.errors.password ? (
                <div className="text-danger small">{formik.errors.password}</div>
              ) : null}
            </div>
            <div className="col-md-6">
              <label htmlFor="confirm_password" className="form-label">
                Confirm Password
              </label>
              <div className="d-flex justify-content-center align-items-center position-relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control input"
                  id="confirm_password"
                  placeholder="Confirm Password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirm_password}
                />
                <span
                  className="eye_btn"
                  onClick={() => {
                    setShowConfirmPassword((prev) => !prev);
                  }}
                >
                  {!showConfirmPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </span>
              </div>
              {formik.touched.confirm_password && formik.errors.confirm_password ? (
                <div className="text-danger small">{formik.errors.confirm_password}</div>
              ) : null}
            </div>
          </div>

          {/* Website */}
          <div className="mb-3">
            <label htmlFor="website" className="form-label">
              Website Name (Optional)
            </label>
            <input
              type="text"
              className="form-control input"
              id="website"
              placeholder="Enter Website"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.website}
            />
          </div>

          {/* Total Members */}
          <div className="mb-3">
            <label htmlFor="total_members" className="form-label">
              Member Count (Including Admin)
            </label>
            <input
              type="number"
              className="form-control input"
              id="total_members"
              placeholder="Enter Members Count"
              min="1"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.total_members}
            />
            {formik.touched.total_members && formik.errors.total_members ? (
              <div className="text-danger small">{formik.errors.total_members}</div>
            ) : null}
          </div>

          {/* Is Agency */}
          <div className="mb-4">
            <label className="form-label">Are you an Agency?</label>
            <div className="d-flex gap-4 align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="radio"
                  name="is_agency"
                  id="is_agency_yes"
                  value="yes"
                  checked={formik.values.is_agency === 'yes'}
                  onChange={formik.handleChange}
                />
                <label htmlFor="is_agency_yes" className="mb-0">Yes</label>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="radio"
                  name="is_agency"
                  id="is_agency_no"
                  value="no"
                  checked={formik.values.is_agency === 'no'}
                  onChange={formik.handleChange}
                />
                <label htmlFor="is_agency_no" className="mb-0">No</label>
              </div>
            </div>
            {formik.touched.is_agency && formik.errors.is_agency ? (
              <div className="text-danger small">{formik.errors.is_agency}</div>
            ) : null}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="btn btn-dark d-flex gap-2 justify-content-center align-items-center w-100 login_btn"
          >
            {loading ? "Creating Account..." : "Create Account"}
            <div>
              <img
                src={group}
                style={{ height: "10px", width: "14px" }}
                alt=""
                srcSet=""
              />
            </div>
          </button>
        </div>

        <p id="bottom_text" className="text-center pb-3 pt-2">
          Already have an account? <Link to="/client/login">Log In</Link>
        </p>
      </div>
      </form>
    </div>
  );
};

export default ClientSignup;